import {view, Badge, Divider, Action} from '@sail/ui';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
  useInlineDataEvents,
} from '@stripe-internal/inline-data';
import {
  widgetLinkAction,
  LightCard,
  ChatButtonOptions,
  TextRow,
} from '@stripe-internal/conversations-ui';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {useState, useRef, useEffect} from 'react';
import {v4 as uuid} from 'uuid';
import SendUsAnEmailSection, {
  EmailForm,
  FollowUpEmailClientErrors,
} from '../helpers/SendUsAnEmailSection';

export type ValueProps = {
  has_follow_up_case: boolean;
  sco_id?: string;
  subject?: string;
  last_modified_date?: number;
  case_creator?: string;
  support_center_link?: string;
  show_contact_support_options?: boolean;
  email_form?: EmailForm;
  error_code?: FollowUpEmailClientErrors;
  is_email_form_submitted?: boolean;
  show_cc_emails_field?: boolean;
  existing_cc_emails?: string[];
};

const messages = defineMessages({
  CaseCardTitle: {
    id: 'mse_inline_data.merchant_follow_up_case.case_card_title',
    description: 'Title for the card displaying the follow-up case.',
    defaultMessage: 'Support case',
  },
  lastModifiedDateLabel: {
    id: 'mse_inline_data.merchant_follow_up_case.last_modified_date_label',
    description:
      'Label for displaying the last modified date of a follow-up case.',
    defaultMessage: 'Last updated',
  },
  caseIDLabel: {
    id: 'mse_inline_data.merchant_follow_up_case.caseID_label',
    description: 'Label for displaying the case ID of a follow-up case.',
    defaultMessage: 'Case ID',
  },
  statusLabel: {
    id: 'mse_inline_data.merchant_follow_up_case.status_label',
    description: 'Label for displaying the status of a follow-up case.',
    defaultMessage: 'Status',
  },
  openStatus: {
    id: 'mse_inline_data.merchant_follow_up_case.open_status',
    description: 'Status for an open case.',
    defaultMessage: 'Open',
  },
  openStatusDescription: {
    id: 'mse_inline_data.merchant_follow_up_case.open_status_description',
    description: 'Status description for an open case.',
    defaultMessage:
      'This case has been assigned to a support agent who is actively investigating.',
  },
  followUpOnCaseOption: {
    id: 'mse_inline_data.merchant_follow_up_case.follow_up_on_case',
    description: 'Option for following up on the existing case',
    defaultMessage: 'Follow up about this case',
  },
  differentCaseOption: {
    id: 'mse_inline_data.merchant_follow_up_case.check_status',
    description: 'Option for checking the status of a different case',
    defaultMessage: 'Check status of a different case',
  },
  newIssueOption: {
    id: 'mse_inline_data.merchant_follow_up_case.new_issue',
    description: 'Option for indicating this is a new issue',
    defaultMessage: 'I have a new issue',
  },
  contactSupportFollowUpOnCaseOption: {
    id: 'mse_inline_data.merchant_follow_up_case.yes_follow_up_on_case',
    description: 'Option for following up on the existing case',
    defaultMessage: 'Yes, follow up on this case',
  },
  contactSupportDifferentCaseOption: {
    id: 'mse_inline_data.merchant_follow_up_case.no_different_case',
    description: 'Option for indicating this is a different case',
    defaultMessage: 'No, I have a different case',
  },
  contactSupportNewIssueOption: {
    id: 'mse_inline_data.merchant_follow_up_case.no_new_issue',
    description: 'Option for indicating this is a new issue',
    defaultMessage: 'No, this is a new issue',
  },
});

type OptionIds = 'channel_email' | 'different_case' | 'new_issue';

export const MerchantFollowUpCase = (props: InlineDatumReactComponentProps) => {
  const {datum, onDone, action, isDone, actionError} = props;
  const value = datum.value as ValueProps;
  const events = useInlineDataEvents();
  const intl = useIntl();
  const [selectedOption, setSelectedOption] = useState<OptionIds | undefined>(
    undefined,
  );
  const {
    has_follow_up_case: hasFollowUpCase,
    sco_id: caseID,
    last_modified_date: lastModifiedDate,
    case_creator: caseCreator,
    subject,
    support_center_link: supportCenterLink,
    show_contact_support_options: showContactSupportOptions,
    email_form: emailForm,
    error_code: errorCode,
    is_email_form_submitted: isEmailFormSubmitted,
    show_cc_emails_field: showCcEmailsField,
    existing_cc_emails: existingCcEmails,
  } = value;

  const afterInlineDataMessage = defineMessages({
    visitSupportCenter: {
      id: 'mse_inline_data.merchant_follow_up_case.after_inline_data_message',
      description: 'Tells the merchant to visit the support center.',
      defaultMessage: `To learn more about all your support cases, we recommend viewing them in your [Support center]({supportCenterLink}).`,
    },
    checkStatusOfDifferentCase: {
      id: 'mse_inline_data.merchant_follow_up_case.different_case',
      description: 'Asks the merchant if they have a different case.',
      defaultMessage:
        'Would you like to check the status of a different support case?',
    },
  });

  useEffect(() => {
    if (selectedOption !== 'channel_email' || !isEmailFormSubmitted || isDone) {
      return;
    }

    onDone({
      type: 'success',
      extra: {
        outcome: selectedOption,
        value,
      },
    });
  }, [isDone, isEmailFormSubmitted, onDone, selectedOption, value]);
  const nonce = useRef<string>(uuid()).current;

  const localizedModifiedDate =
    lastModifiedDate &&
    intl.formatDate(new Date(lastModifiedDate * 1000), {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });

  const analytics = useAnalytics();

  analytics.viewed('uh_merchant_follow_up_case', {
    hasFollowUpCase,
  });

  if (!hasFollowUpCase) {
    return null;
  }

  const onConvOptionPress = (optionId: OptionIds) => {
    events.emit({name: 'inline-data-trigger-scroll-to-top'});
    setSelectedOption(optionId);
    analytics.action('uh_merchant_follow_up_case_option_selected', {
      followUpCaseOption: optionId,
    });
    if (optionId !== 'channel_email') {
      onDone({
        type: 'success',
        extra: {
          outcome: optionId,
        },
      });
    } else {
      events.emitWithResult({
        name: 'inline-data-action',
        data: {
          purpose: 'channel_selection',
          channelID: optionId,
        },
        nonce,
      });
    }
  };

  const optionMessages = showContactSupportOptions
    ? [
        messages.contactSupportFollowUpOnCaseOption,
        messages.contactSupportDifferentCaseOption,
        messages.contactSupportNewIssueOption,
      ]
    : [
        messages.followUpOnCaseOption,
        messages.differentCaseOption,
        messages.newIssueOption,
      ];

  const optionIds: OptionIds[] = [
    'channel_email',
    'different_case',
    'new_issue',
  ];

  const convOptions = optionIds.map((optionId, index) => {
    return {
      value: optionId,
      label: intl.formatMessage(optionMessages[index]),
      onPress: () => onConvOptionPress(optionId),
    };
  });

  const handleCancelForm = () => {
    analytics.action('uh_merchant_follow_up_case_cancel_form');
    setSelectedOption(undefined);
  };

  const ctaLink = `${supportCenterLink}/${caseID}`;

  return (
    <view.div css={{stack: 'y', gap: 'medium'}}>
      <Action
        href={ctaLink}
        css={{
          textColor: 'text',
        }}
        disabled={!caseID}
        uses={[widgetLinkAction()]}
        onPress={(_) =>
          analytics.action('uh_merchant_follow_up_case_cta_button', {
            ctaLink,
          })
        }
      >
        <LightCard hover isOuter>
          <view.div css={{stack: 'y', gap: 'small'}}>
            <view.div css={{stack: 'x', gap: 'small'}}>
              <view.div
                css={{
                  stack: 'x',
                  gap: 'small',
                  font: 'label.small.emphasized',
                }}
              >
                <FormattedMessage {...messages.CaseCardTitle} />
                {caseCreator ? (
                  <>
                    <Divider />
                    <view.div
                      data-testid="case-creator"
                      css={{textColor: 'action.primary'}}
                    >
                      {caseCreator}
                    </view.div>
                  </>
                ) : null}
              </view.div>
              <Badge data-testid="status-badge" type="info">
                <FormattedMessage {...messages.openStatus} />
              </Badge>
            </view.div>
            <view.div css={{stack: 'y', gap: 'small'}}>
              <view.div
                data-testid="case-subject"
                css={{font: 'heading.small'}}
              >
                {subject}
              </view.div>
              <view.div css={{stack: 'x', gap: 'small'}}>
                <view.div css={{stack: 'y'}}>
                  <view.div css={{font: 'label.medium.emphasized'}}>
                    <FormattedMessage {...messages.caseIDLabel} />
                  </view.div>
                  <view.div
                    data-testid="case-id"
                    css={{font: 'label.medium', textColor: 'subdued'}}
                  >
                    {caseID}
                  </view.div>
                </view.div>
                {lastModifiedDate ? (
                  <view.div css={{stack: 'y'}}>
                    <view.div css={{font: 'label.medium.emphasized'}}>
                      <FormattedMessage {...messages.lastModifiedDateLabel} />
                    </view.div>
                    <view.div
                      data-testid="last-modified-date"
                      css={{font: 'label.medium', textColor: 'subdued'}}
                    >
                      {localizedModifiedDate}
                    </view.div>
                  </view.div>
                ) : null}
              </view.div>
              <view.div
                data-testid="status-description"
                css={{font: 'label.small', color: 'text.subdued'}}
              >
                <FormattedMessage {...messages.openStatusDescription} />
              </view.div>
            </view.div>
          </view.div>
        </LightCard>
      </Action>

      {!showContactSupportOptions && (
        <>
          <TextRow
            // eslint-disable-next-line react/style-prop-object
            style="none"
            body={intl.formatMessage(
              afterInlineDataMessage.visitSupportCenter,
              {
                supportCenterLink,
              },
            )}
          />
          <TextRow
            // eslint-disable-next-line react/style-prop-object
            style="none"
            body={intl.formatMessage(
              afterInlineDataMessage.checkStatusOfDifferentCase,
            )}
          />
        </>
      )}
      <ChatButtonOptions
        options={convOptions}
        selectedOptionId={selectedOption}
        animated
      />
      <SendUsAnEmailSection
        action={action}
        nonce={nonce}
        handleCancelForm={handleCancelForm}
        emailForm={emailForm}
        isSectionVisible={selectedOption === 'channel_email'}
        isEmailFormSubmitted={isDone || !!isEmailFormSubmitted}
        showUnauthenticatedEmailField={false}
        showCcEmailsField={showCcEmailsField}
        existingCcEmails={existingCcEmails}
        showContactSupportOptions={showContactSupportOptions}
        clientError={
          errorCode ? (errorCode as FollowUpEmailClientErrors) : undefined
        }
        serverError={actionError}
      />
    </view.div>
  );
};
